import { faAngleDown, faAngleUp } from '@fortawesome/pro-regular-svg-icons';
import Invoice from '@spike/invoice-model';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { FunctionComponent, useState } from 'react';
import InvoiceResume from '../InvoiceResume';
import Promocodes from '../Promocodes/Promocodes';
import PriceField from '../SplitPayments/PriceField';
import ResumeLine from '../UI/ResumeLine';
import TotalResumeLine from '../UI/TotalResumeLine';
import { formatAmount } from '../utils/OrderUtils';
import styles from './CheckoutPaymentFooter.module.scss';

export interface CheckoutPaymentFooterProps {
    invoice?: Invoice;
    isBooking: boolean;
    showInvoiceResume: boolean;
    showAmountToPay: boolean;
    showPayButton: boolean;
    amountToPay: number;
    amountToPayError?: string;
    errors: Array<string>;
    buttonLabel: string;
    buttonDisabled: boolean;
    buttonLoading: boolean;
    loadingInvoice: boolean;
    loadingTip: boolean;
    className?: string;
    onAmountToPayChange: (value: number) => void;
    onPay: () => void;
    onAddPromocode: () => void;
    onStartRemovingPromocode: () => void;
    onStopRemovingPromocode: () => void;
}

export const CheckoutPaymentFooter: FunctionComponent<CheckoutPaymentFooterProps> = ({
    invoice,
    isBooking,
    showInvoiceResume,
    showAmountToPay,
    showPayButton,
    amountToPay,
    amountToPayError,
    errors,
    buttonLabel,
    buttonDisabled,
    buttonLoading,
    loadingInvoice,
    loadingTip,
    className,
    onAmountToPayChange,
    onPay,
    onAddPromocode,
    onStartRemovingPromocode,
    onStopRemovingPromocode
}) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const balance = Number(invoice?.balance ?? '0.00');
    const hasCredit = balance < 0;
    const loadingTotals = loadingInvoice || loadingTip;

    return (
        <div className={clsx(styles.root, className)}>
            {showInvoiceResume && (
                <Button
                    type="button"
                    label=""
                    onlyIcon
                    size="small"
                    variant="outlined"
                    className={styles.toggle}
                    leftIcon={isExpanded ? faAngleDown : faAngleUp}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? 'Hide payment details' : 'Show payment details'}
                    onClick={() => setIsExpanded(expanded => !expanded)}
                />
            )}

            <div className={styles.content}>
                {showInvoiceResume && isBooking && invoice?.id && !loadingTip && (
                    <Promocodes
                        invoice={invoice}
                        onAdd={onAddPromocode}
                        className={styles.promocodes}
                        onStartRemoving={onStartRemovingPromocode}
                        onStopRemoving={onStopRemovingPromocode}
                    />
                )}

                {showInvoiceResume &&
                    (isExpanded ? (
                        <InvoiceResume
                            invoice={invoice}
                            loadingInvoice={loadingInvoice}
                            loadingTip={loadingTip}
                            className={styles.invoiceResume}
                        />
                    ) : (
                        <div className={styles.summary}>
                            <ResumeLine
                                description="Total"
                                value={formatAmount(invoice?.grandTotal)}
                                loading={loadingTotals}
                            />
                            <TotalResumeLine
                                description="Balance Due"
                                value={hasCredit ? '0.00' : formatAmount(balance.toFixed(2)) ?? '0.00'}
                                loading={loadingTotals}
                            />
                        </div>
                    ))}

                {errors.length > 0 && (
                    <div className={styles.errors}>
                        {errors.map((error, index) => (
                            <p
                                className={styles.error}
                                key={index}
                            >
                                {error}
                            </p>
                        ))}
                    </div>
                )}

                {showAmountToPay && (
                    <PriceField
                        id="checkout-payment-price-field"
                        label="Amount to Pay"
                        name="amount"
                        value={amountToPay}
                        placeholder="0.00"
                        onChange={value => onAmountToPayChange(value)}
                        error={amountToPayError}
                    />
                )}

                {showPayButton && (
                    <Button
                        type="button"
                        label={buttonLabel}
                        variant="black"
                        fullWidth
                        className={styles.payButton}
                        disabled={buttonDisabled}
                        isLoading={buttonLoading}
                        onClick={onPay}
                    />
                )}
            </div>
        </div>
    );
};

export default CheckoutPaymentFooter;
